/**
 * FitnessLogModal — generic add/edit-record modal used by Orders / Training / Nutrition pages.
 * Pass `fields` = array of { key, labelAr, labelEn, type, options? }
 *  - type: 'text' | 'number' | 'select' | 'textarea' | 'autocomplete'
 *  - options (for select): [{value, ar, en}]
 *  - autocompleteSource (for autocomplete): URL fetched once for <datalist> suggestions
 *
 * When `initialValues` is set, the modal switches to EDIT mode (title prefix "تعديل" / "Edit",
 * and submits the values straight to onSubmit which the parent routes to PUT instead of POST).
 */
import { useState, useEffect, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Save, Plus, Edit3 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FitnessLogModal({
  open, onClose, onSubmit, title, titleEn, fields, saving = false, color = 'emerald',
  initialValues = null, // when set, edit mode
}) {
  const { language } = useLanguage();
  const [values, setValues] = useState({});
  const [memberOptions, setMemberOptions] = useState([]);
  const datalistId = useId();
  const isEdit = !!initialValues;

  // Reset values whenever the modal opens or initialValues change
  useEffect(() => {
    if (open) {
      const seed = {};
      fields.forEach((f) => {
        if (initialValues && initialValues[f.key] !== undefined) {
          seed[f.key] = initialValues[f.key];
        } else {
          seed[f.key] = f.default ?? (f.type === 'number' ? '' : '');
        }
      });
      setValues(seed);
    }
  }, [open, fields, initialValues]);

  // Fetch member list once when an autocomplete field is present
  useEffect(() => {
    if (!open) return;
    const hasAuto = fields.some((f) => f.type === 'autocomplete');
    if (!hasAuto) return;
    axios
      .get(`${API_URL}/api/fitness/members-list`, { headers: getAuthHeaders() })
      .then((res) => setMemberOptions(res.data?.data || []))
      .catch(() => setMemberOptions([]));
  }, [open, fields]);

  const accent = {
    emerald: { btn: 'bg-emerald-500 hover:bg-emerald-600', icon: 'text-emerald-400', border: 'focus:border-emerald-500/50' },
    amber:   { btn: 'bg-amber-500 hover:bg-amber-600',     icon: 'text-amber-400',   border: 'focus:border-amber-500/50' },
    blue:    { btn: 'bg-blue-500 hover:bg-blue-600',       icon: 'text-blue-400',    border: 'focus:border-blue-500/50' },
  }[color];

  const handleSubmit = async () => {
    await onSubmit(values);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !saving && onClose()}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto"
            data-testid="fitness-log-modal"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                {isEdit ? <Edit3 className={`h-5 w-5 ${accent.icon}`} /> : <Plus className={`h-5 w-5 ${accent.icon}`} />}
                {isEdit
                  ? (language === 'ar'
                      ? title.replace('إضافة ', 'تعديل ').replace(/\s+(جديد|جديدة)\b/, '')
                      : titleEn.replace('Add New ', 'Edit ').replace('Add ', 'Edit '))
                  : (language === 'ar' ? title : titleEn)}
              </h3>
              <button onClick={onClose} className="p-1 text-[#71717A] hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Shared datalist for autocomplete fields */}
            {memberOptions.length > 0 && (
              <datalist id={datalistId}>
                {memberOptions.map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
            )}

            <div className="grid grid-cols-2 gap-3">
              {fields.map((field) => {
                const colSpan = field.fullWidth ? 'col-span-2' : '';
                return (
                  <label key={field.key} className={`block ${colSpan}`}>
                    <span className="block text-xs text-[#A1A1AA] mb-1">
                      {language === 'ar' ? field.labelAr : field.labelEn}
                      {field.required && <span className="text-red-400 mx-1">*</span>}
                    </span>
                    {field.type === 'select' ? (
                      <select
                        value={values[field.key] ?? ''}
                        onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                        className={`w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${accent.border}`}
                        data-testid={`log-form-${field.key}`}
                      >
                        {field.options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {language === 'ar' ? opt.ar : opt.en}
                          </option>
                        ))}
                      </select>
                    ) : field.type === 'textarea' ? (
                      <textarea
                        rows={3}
                        value={values[field.key] ?? ''}
                        onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                        className={`w-full bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${accent.border} resize-none`}
                        data-testid={`log-form-${field.key}`}
                      />
                    ) : field.type === 'autocomplete' ? (
                      <input
                        type="text"
                        list={datalistId}
                        value={values[field.key] ?? ''}
                        onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                        placeholder={language === 'ar' ? field.placeholderAr : field.placeholderEn}
                        className={`w-full bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${accent.border}`}
                        data-testid={`log-form-${field.key}`}
                        autoComplete="off"
                      />
                    ) : (
                      <input
                        type={field.type || 'text'}
                        min={field.type === 'number' ? 0 : undefined}
                        step={field.step}
                        value={values[field.key] ?? ''}
                        onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                        placeholder={language === 'ar' ? field.placeholderAr : field.placeholderEn}
                        className={`w-full bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${accent.border}`}
                        data-testid={`log-form-${field.key}`}
                      />
                    )}
                  </label>
                );
              })}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={onClose}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-[#A1A1AA] hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className={`flex-1 py-3 rounded-xl text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${accent.btn}`}
                data-testid="log-form-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isEdit ? (language === 'ar' ? 'حفظ التعديلات' : 'Save Changes') : (language === 'ar' ? 'حفظ' : 'Save')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
